/* eslint-disable camelcase */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  FormEvent,
} from 'react';
import { FiX, FiSend, FiCopy, FiCheck, FiChevronLeft } from 'react-icons/fi';
import api from '../../services/apiClient';

import {
  Overlay,
  Sidebar,
  Header,
  Content,
  EmptyState,
  MessageBubble,
  Options,
  Chip,
  ResultCard,
  CopyButton,
  InputArea,
  Loading,
} from './styles';

interface NcmChatSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ChatMessage {
  from: 'user' | 'agent';
  text: string;
  options?: string[];
}

interface NcmResult {
  ncm: string;
  descricao: string;
  aliquota_ipi: string;
  confianca: number;
  raciocinio: string;
}

interface NcmChatResponse {
  sessao_id: string;
  tipo: 'pergunta' | 'resultado' | 'classificacao';
  mensagem?: string
  pergunta?: string;
  opcoes?: string[];
  candidatos_restantes?: number;
  ncm?: string;
  descricao?: string;
  aliquota_ipi?: string;
  confianca?: number;
  raciocinio?: string;
}

const NcmChatSidebar: React.FC<NcmChatSidebarProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [result, setResult] = useState<NcmResult | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const resetState = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setResult(null);
    setInputValue('');
    setLoading(false);
    setCopied(false);
  }, []);

  // Focus input and reset when opening/closing
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
    resetState();
    return undefined;
  }, [isOpen, resetState]);

  // Close on Escape while open
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Auto-scroll to the last message
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [messages, result, loading]);

  const handleSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) {
        return;
      }

      // Se já houve um resultado, começa uma nova sessão
      const startingNewSession = result !== null;
      const activeSessionId = startingNewSession ? null : sessionId;

      if (startingNewSession) {
        setResult(null);
        setMessages([{ from: 'user', text: trimmed }]);
      } else {
        setMessages(prev => [...prev, { from: 'user', text: trimmed }]);
      }

      setInputValue('');
      setLoading(true);

      try {
        const { data } = await api.post<NcmChatResponse>('/ncm/chat', {
          mensagem: trimmed,
          sessao_id: activeSessionId,
        });

        setSessionId(data.sessao_id);

        if (data.tipo === 'resultado' || data.tipo === 'classificacao') {
          setResult({
            ncm: data.ncm ?? '',
            descricao: data.descricao ?? '',
            aliquota_ipi: data.aliquota_ipi ?? '',
            confianca: data.confianca ?? 0,
            raciocinio: data.raciocinio ?? '',
          });
        } else {
          setMessages(prev => [
            ...prev,
            {
              from: 'agent',
              text: data.mensagem ?? '',
              options: data.opcoes,
            },
          ]);
        }
      } catch (err) {
        setMessages(prev => [
          ...prev,
          {
            from: 'agent',
            text: 'Ocorreu um erro ao consultar o agente. Tente novamente.',
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [loading, result, sessionId],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSend(inputValue);
    },
    [handleSend, inputValue],
  );

  const handleCopy = useCallback(() => {
    if (!result) {
      return;
    }
    navigator.clipboard?.writeText(result.ncm);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [result]);

  return (
    <>
      <Overlay isOpen={isOpen} onClick={onClose} />
      <Sidebar isOpen={isOpen}>
        <Header>
          <h2>Descobrir NCM</h2>
          <button type="button" onClick={onClose} aria-label="Fechar">
            <FiX />
          </button>
        </Header>

        <Content ref={contentRef}>
          {messages.length === 0 && !result && !loading ? (
            <EmptyState>
              <FiChevronLeft />
              <p>
                Descreva o item que você quer classificar. O agente fará
                perguntas até chegar ao NCM ideal.
              </p>
            </EmptyState>
          ) : (
            <>
              {messages.map((message, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                  <MessageBubble from={message.from}>
                    {message.text}
                  </MessageBubble>
                  {message.options && message.options.length > 0 && (
                    <Options>
                      {message.options.map(option => (
                        <Chip
                          key={option}
                          type="button"
                          disabled={loading}
                          onClick={() => handleSend(option)}
                        >
                          {option}
                        </Chip>
                      ))}
                    </Options>
                  )}
                </React.Fragment>
              ))}

              {result && (
                <ResultCard>
                  <div className="row">
                    <span>NCM</span>
                    <div className="ncm">{result.ncm}</div>
                  </div>
                  <div className="row">
                    <span>Descrição</span>
                    <strong>{result.descricao}</strong>
                  </div>
                  <div className="row">
                    <span>Alíquota IPI</span>
                    <strong>{`${result.aliquota_ipi}%`}</strong>
                  </div>
                  <div className="row">
                    <span>Confiança</span>
                    <strong>{`${Math.round(result.confianca * 100)}%`}</strong>
                  </div>
                  <div className="row">
                    <span>Raciocínio</span>
                    <p>{result.raciocinio}</p>
                  </div>
                  <CopyButton type="button" onClick={handleCopy}>
                    {copied ? <FiCheck /> : <FiCopy />}
                    {copied ? 'Copiado!' : 'Copiar NCM'}
                  </CopyButton>
                </ResultCard>
              )}

              {loading && <Loading>O agente está pensando...</Loading>}
            </>
          )}
        </Content>

        <InputArea onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            value={inputValue}
            disabled={loading}
            placeholder="Descreva o item..."
            onChange={event => setInputValue(event.target.value)}
          />
          <button type="submit" disabled={loading} aria-label="Enviar">
            <FiSend />
          </button>
        </InputArea>
      </Sidebar>
    </>
  );
};

export default NcmChatSidebar;
